/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { type TSlide } from "@/zapi-frontend/stores/presentation";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { SlideOutlineEditor } from "./SlideOutlineEditor";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { hasEditAccess } from "@/zapi-frontend/shared/utils/permissions";

type TSlideCardProps = {
  slide: TSlide;
  index: number;
  onUpdate: (slideId: string, updates: Partial<TSlide>) => void;
  onDelete: (slideId: string) => void;
  selectedIndex: number;
  onSelect: (index: number) => void;
  isReadOnly: boolean;
};

export function SlideCard(props: TSlideCardProps) {
  const { t } = useTranslation("projectHome");
  const { slide, onUpdate, index, selectedIndex, onSelect, isReadOnly } = props;
  const { role, accessMode } = useMeStore();
  const hasEdit = hasEditAccess(role, accessMode);
  const isSelected = selectedIndex === index;
  // A read-only card behaves like an unselected one, keeping its toolbar so the layout does not jump.
  const isEditable = isSelected && !isReadOnly;

  const [title, setTitle] = useState(slide.title || "");
  const [outline, setOutline] = useState(slide.outline || "");
  const titleRef = useRef<HTMLTextAreaElement>(null);

  const adjustTitleHeight = useCallback(() => {
    const textarea = titleRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  }, []);

  useEffect(() => {
    if (slide.title !== title) setTitle(slide.title || "");
    if (slide.outline !== outline) setOutline(slide.outline || "");
  }, [slide.title, slide.outline]);

  useEffect(() => {
    adjustTitleHeight();
  }, [title, adjustTitleHeight]);

  useEffect(() => {
    const textarea = titleRef.current;
    if (!textarea) return;

    // Only a width change rewraps the title. Measuring collapses it for a moment, which at the end of
    // a scrolled list pulls the scroll position up, so height changes of the card are skipped.
    let width = 0;
    const resizeObserver = new ResizeObserver(([entry]) => {
      if (entry.contentRect.width === width) return;
      width = entry.contentRect.width;
      adjustTitleHeight();
    });

    if (textarea.parentElement) resizeObserver.observe(textarea.parentElement);

    return () => resizeObserver.disconnect();
  }, [adjustTitleHeight]);

  function changeTitleHandler(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const { value } = e.target;
    setTitle(value);
    onUpdate(slide.id, { title: value });
  }

  const changeOutlineHandler = useCallback(
    (value: string) => {
      setOutline(value);
      onUpdate(slide.id, { outline: value });
    },
    [slide.id, onUpdate]
  );

  return (
    <article
      data-testid={`slide-${slide.id}-item`}
      id={`slide-${slide.id}`}
      aria-current={isSelected || undefined}
      aria-label={t("presentation.slideNumber", { number: index + 1 })}
      onClick={() => !isSelected && onSelect(index)}
      className={cn(
        "flex min-h-72 scroll-mt-4 flex-col gap-3 rounded-xl bg-card px-6 pt-5 pb-4 ring-1 ring-foreground/10 transition-shadow",
        isSelected ? "shadow-md ring-2 ring-primary/60" : "cursor-pointer hover:ring-foreground/20"
      )}
    >
      <span className={cn("typo-overline", isSelected ? "text-primary-text" : "text-muted-foreground")}>
        {t("presentation.slideNumber", { number: index + 1 })}
      </span>
      <textarea
        data-testid={`slide-${slide.id}-title-input`}
        ref={titleRef}
        aria-label={t("presentationBuilder.slideTitleLabel", { number: index + 1 })}
        className="typo-h4 -mx-2 w-[calc(100%+1rem)] resize-none overflow-hidden rounded-lg bg-transparent px-2 py-1 text-foreground outline-none transition-colors placeholder:text-muted-foreground read-only:cursor-pointer hover:read-only:bg-transparent focus-visible:bg-muted/50 focus-visible:ring-3 focus-visible:ring-ring/40 enabled:not-read-only:hover:bg-muted/50"
        value={title}
        onChange={changeTitleHandler}
        placeholder={t("presentationBuilder.slideTitlePlaceholder")}
        rows={1}
        readOnly={!hasEdit || !isEditable}
      />
      <div className="flex min-h-0 flex-1 flex-col">
        <SlideOutlineEditor
          value={outline}
          onChange={changeOutlineHandler}
          placeholder={t("presentationBuilder.keyPointsPlaceholder")}
          isReadOnly={!hasEdit}
          isSelected={isEditable}
        />
      </div>
    </article>
  );
}
